import { FormEvent, useState } from 'react'
import { CalendarX, Laptop, Pencil, Repeat } from 'lucide-react'
import { Alerta, AreaTexto, Boton, Busqueda, Campo, Cargando, Casilla, Dialogo, Entrada, Panel } from '../kit'
import {
  useCrearTeletrabajo, useEditarTeletrabajo, usePersonasTeletrabajo, useTeletrabajoBien, useTerminarTeletrabajo,
} from '../../queries/useInventario'
import { Bien, DiaHabil, Domicilio, FichaTeletrabajo, PersonaTeletrabajo } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { formatoDia, isoDia } from '../../utils/fecha'
import { DIAS, nombrarDias, viajes } from '../../utils/teletrabajo'

/**
 * La ficha de teletrabajo de un bien (para quien administra bienes): quién se lo lleva,
 * qué días y su domicilio, que es lo que pide la guía de despacho. Las guías de cada
 * semana están en *Teletrabajo*.
 */
export function PanelTeletrabajo({ bien }: { bien: Bien }) {
  const fichas = useTeletrabajoBien(bien.id)
  const [dialogo, setDialogo] = useState<'crear' | 'editar' | 'terminar' | null>(null)
  const vigente = fichas.data?.vigente ?? null

  return (
    <Panel
      titulo="Teletrabajo"
      acciones={
        vigente ? (
          <>
            <Boton tam="sm" variante="outlined" icono={Pencil} onClick={() => setDialogo('editar')}>Editar</Boton>
            <Boton tam="sm" variante="text" color="error" icono={CalendarX} onClick={() => setDialogo('terminar')}>Terminar</Boton>
          </>
        ) : (
          bien.estado !== 'BAJA' && <Boton tam="sm" variante="outlined" icono={Laptop} onClick={() => setDialogo('crear')}>Armar ficha</Boton>
        )
      }
    >
      {fichas.isLoading && <Cargando />}
      {fichas.isError && <Alerta tipo="error">{mensajeDeError(fichas.error)}</Alerta>}
      {fichas.data && !vigente && <p className="texto-secundario">No se lo lleva nadie a la casa.</p>}
      {vigente && <DatosFicha ficha={vigente} />}
      {fichas.data && fichas.data.anteriores.length > 0 && (
        <details className="teletrabajo__anteriores">
          <summary>Fichas anteriores ({fichas.data.anteriores.length})</summary>
          <ul>
            {fichas.data.anteriores.map((f) => (
              <li key={f.id}>
                {f.persona.nombre} · {nombrarDias(f.dias)} · {formatoDia(f.desde)} al {formatoDia(f.hasta)}
              </li>
            ))}
          </ul>
        </details>
      )}

      {dialogo === 'crear' && <DialogoTeletrabajo bien={bien} onCerrar={() => setDialogo(null)} />}
      {dialogo === 'editar' && vigente && <DialogoTeletrabajo bien={bien} ficha={vigente} onCerrar={() => setDialogo(null)} />}
      {dialogo === 'terminar' && vigente && <DialogoTerminar ficha={vigente} onCerrar={() => setDialogo(null)} />}
    </Panel>
  )
}

function DatosFicha({ ficha }: { ficha: FichaTeletrabajo }) {
  const p = ficha.persona
  return (
    <dl className="datos">
      <dt>Persona</dt>
      <dd>
        {p.nombre}
        <span className="tabla__sub">{p.rut}</span>
      </dd>
      <dt>Domicilio</dt>
      <dd>{[p.direccion, p.comuna, p.ciudad].filter(Boolean).join(', ') || '—'}</dd>
      <dt>Correo</dt>
      <dd>{p.email || '—'}</dd>
      <dt>Teletrabaja</dt>
      <dd>
        {nombrarDias(ficha.dias)}
        {viajes(ficha.dias).map((v) => (
          <span key={v} className="tabla__sub">{v}</span>
        ))}
      </dd>
      <dt>Desde</dt>
      <dd>{formatoDia(ficha.desde)}</dd>
      {ficha.observacion && (
        <>
          <dt>Observación</dt>
          <dd className="texto-preformateado">{ficha.observacion}</dd>
        </>
      )}
    </dl>
  )
}

/** Elegir a la persona (solo cuentas de la app) y después los días y el domicilio. Editar parte en el segundo paso. */
function DialogoTeletrabajo({ bien, ficha, onCerrar }: { bien: Bien; ficha?: FichaTeletrabajo; onCerrar: () => void }) {
  const [persona, setPersona] = useState<PersonaTeletrabajo | null>(ficha?.persona ?? null)
  const titulo = `${ficha ? 'Ficha' : 'Nueva ficha'} de teletrabajo · ${bien.numero || 'Sin N°'} ${bien.descripcion}`
  return (
    <Dialogo abierto onCerrar={onCerrar} titulo={titulo}>
      {persona ? (
        <FormularioFicha bien={bien} ficha={ficha} persona={persona} onCambiarPersona={ficha ? undefined : () => setPersona(null)} onCerrar={onCerrar} />
      ) : (
        <ElegirPersona onElegir={setPersona} />
      )}
    </Dialogo>
  )
}

function ElegirPersona({ onElegir }: { onElegir: (p: PersonaTeletrabajo) => void }) {
  const [q, setQ] = useState('')
  const personas = usePersonasTeletrabajo(q)
  return (
    <div className="pila">
      <p className="texto-secundario">¿Quién se lo lleva? Tiene que tener cuenta en la aplicación (se registra en Usuarios).</p>
      <Busqueda etiqueta="Buscar persona por nombre o RUT" placeholder="Nombre o RUT" value={q} onChange={(e) => setQ(e.target.value)} data-autofocus />
      {personas.isFetching && <Cargando texto="Buscando…" />}
      {personas.isError && <Alerta tipo="error">{mensajeDeError(personas.error)}</Alerta>}
      {personas.data && q.trim().length >= 2 && (
        personas.data.length ? (
          <ul className="lista-elegir">
            {personas.data.map((p) => (
              <li key={p.id}>
                <span>
                  {p.nombre}
                  <span className="tabla__sub">{p.rut || 'Sin RUT'}</span>
                </span>
                <Boton tam="sm" variante="outlined" onClick={() => onElegir(p)}>Elegir</Boton>
              </li>
            ))}
          </ul>
        ) : (
          <p className="texto-secundario">Nadie con cuenta con ese nombre o RUT.</p>
        )
      )}
    </div>
  )
}

function FormularioFicha({
  bien, ficha, persona, onCambiarPersona, onCerrar,
}: {
  bien: Bien
  ficha?: FichaTeletrabajo
  persona: PersonaTeletrabajo
  onCambiarPersona?: () => void
  onCerrar: () => void
}) {
  const [dias, setDias] = useState<DiaHabil[]>(ficha?.dias ?? [])
  const [domicilio, setDomicilio] = useState<Domicilio>({
    direccion: persona.direccion, comuna: persona.comuna, ciudad: persona.ciudad, email: persona.email,
  })
  const [desde, setDesde] = useState(ficha?.desde ?? isoDia(new Date()))
  const [observacion, setObservacion] = useState(ficha?.observacion ?? '')
  const crear = useCrearTeletrabajo()
  const editar = useEditarTeletrabajo()
  const guardando = crear.isPending || editar.isPending
  const error = crear.error ?? editar.error

  const alternar = (d: DiaHabil) => setDias((ds) => (ds.includes(d) ? ds.filter((x) => x !== d) : [...ds, d].sort() as DiaHabil[]))
  const campo = (c: keyof Domicilio) => ({ value: domicilio[c], onChange: (e: { target: { value: string } }) => setDomicilio({ ...domicilio, [c]: e.target.value }) })

  const enviar = (e: FormEvent) => {
    e.preventDefault()
    if (ficha) editar.mutate({ id: ficha.id, datos: { dias, observacion, ...domicilio } }, { onSuccess: onCerrar })
    else crear.mutate({ bien: bien.id, datos: { persona: persona.id, dias, desde, observacion, ...domicilio } }, { onSuccess: onCerrar })
  }

  const recorrido = viajes(dias)
  return (
    <form className="pila" onSubmit={enviar}>
      <div className="fila-simple">
        <span>
          <strong>{persona.nombre}</strong>
          <span className="tabla__sub">{persona.rut || 'Sin RUT'}</span>
        </span>
        {onCambiarPersona && <Boton tam="sm" variante="text" icono={Repeat} onClick={onCambiarPersona}>Cambiar</Boton>}
      </div>

      <fieldset className="teletrabajo__dias">
        <legend>Días de teletrabajo</legend>
        <div className="teletrabajo__casillas">
          {DIAS.map((d) => (
            <Casilla key={d.dia} etiqueta={d.texto} checked={dias.includes(d.dia)} onChange={() => alternar(d.dia)} />
          ))}
        </div>
        {recorrido.length > 0 && (
          <ul className="teletrabajo__viajes">
            {recorrido.map((v) => <li key={v}>{v}</li>)}
          </ul>
        )}
        {dias.length === 5 && <Alerta tipo="error">Con los cinco días el bien no vuelve: eso es un traslado.</Alerta>}
      </fieldset>

      <h3 className="subformulario__titulo">Para la guía de despacho</h3>
      <p className="texto-secundario">Queda en los datos de {persona.nombre}: sirve para todas sus fichas.</p>
      <Campo etiqueta="Dirección" requerido>
        <Entrada {...campo('direccion')} required autoComplete="off" />
      </Campo>
      <div className="formulario-grilla">
        <Campo etiqueta="Comuna" requerido>
          <Entrada {...campo('comuna')} required autoComplete="off" />
        </Campo>
        <Campo etiqueta="Ciudad" requerido>
          <Entrada {...campo('ciudad')} required autoComplete="off" />
        </Campo>
      </div>
      <Campo etiqueta="Correo" requerido>
        <Entrada type="email" {...campo('email')} required autoComplete="off" />
      </Campo>
      {!ficha && (
        <Campo etiqueta="Desde" ayuda="El primer día en que rige.">
          <Entrada type="date" value={desde} onChange={(e) => setDesde(e.target.value)} required />
        </Campo>
      )}
      <Campo etiqueta="Observación">
        <AreaTexto rows={2} value={observacion} onChange={(e) => setObservacion(e.target.value)} />
      </Campo>

      {error && <Alerta tipo="error">{mensajeDeError(error)}</Alerta>}
      <div className="acciones-formulario">
        <Boton variante="text" onClick={onCerrar}>Cancelar</Boton>
        <Boton type="submit" cargando={guardando} disabled={!dias.length || dias.length === 5}>{ficha ? 'Guardar' : 'Armar ficha'}</Boton>
      </div>
    </form>
  )
}

function DialogoTerminar({ ficha, onCerrar }: { ficha: FichaTeletrabajo; onCerrar: () => void }) {
  const [motivo, setMotivo] = useState('')
  const terminar = useTerminarTeletrabajo()
  const enviar = (e: FormEvent) => {
    e.preventDefault()
    terminar.mutate({ id: ficha.id, motivo }, { onSuccess: onCerrar })
  }
  return (
    <Dialogo abierto onCerrar={onCerrar} titulo="Terminar la ficha de teletrabajo">
      <form className="pila" onSubmit={enviar}>
        <p>
          {ficha.persona.nombre} deja de llevarse {ficha.bien.tipo_equipo}. Desde mañana no tiene más guías; la ficha queda en el
          historial del bien.
        </p>
        <Campo etiqueta="Motivo" ayuda="Opcional.">
          <AreaTexto rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} data-autofocus />
        </Campo>
        {terminar.isError && <Alerta tipo="error">{mensajeDeError(terminar.error)}</Alerta>}
        <div className="acciones-formulario">
          <Boton variante="text" onClick={onCerrar}>Cancelar</Boton>
          <Boton type="submit" color="error" cargando={terminar.isPending}>Terminar</Boton>
        </div>
      </form>
    </Dialogo>
  )
}
